import React, { useState } from 'react';
import { User, MessageSquare, Clock } from 'lucide-react';
import TaskProgressModal from './TaskProgressModal';

const KanbanBoard = ({ tasks, statusColors, updateTask, taskStatusConfig, allMembers, onOpenNotes, onUpdateTask, currentUser, canReorderStatuses = false, onReorderStatuses }) => {
  const [dragOverCol, setDragOverCol] = useState(null);
  const [draggedStatus, setDraggedStatus] = useState(null);
  const [dragOverStatusHeader, setDragOverStatusHeader] = useState(null);
  const [progressTaskId, setProgressTaskId] = useState(null);
  const columns = ['VAZIO', ...Object.keys(statusColors).filter(k => k !== 'VAZIO')];
  
  const handleDragStart = (e, taskId) => {
    e.dataTransfer.setData('dragType', 'task-card');
    e.dataTransfer.setData('taskId', String(taskId));
    e.dataTransfer.effectAllowed = 'move';
    e.currentTarget.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = '1';
    setDragOverCol(null);
    setDraggedStatus(null);
    setDragOverStatusHeader(null);
  };

  const handleDragOver = (e, col) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverCol(col);
  };

  const handleDragLeave = (e, col) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;
    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      setDragOverCol(null);
    }
  };

  const handleDrop = (e, targetStatus) => {
    e.preventDefault();
    setDragOverCol(null);
    const taskId = parseInt(e.dataTransfer.getData('taskId'));
    if (taskId) updateTask(taskId, 'status', targetStatus);
  };

  const handleStatusHeaderDragStart = (e, statusKey) => {
    if (!canReorderStatuses || statusKey === 'VAZIO') return;
    e.dataTransfer.setData('dragType', 'status-column');
    e.dataTransfer.setData('statusKey', statusKey);
    e.dataTransfer.effectAllowed = 'move';
    setDraggedStatus(statusKey);
  };

  const handleStatusHeaderDragOver = (e, statusKey) => {
    const dragType = e.dataTransfer.getData('dragType');
    if (dragType !== 'status-column') return;
    e.preventDefault();
    setDragOverStatusHeader(statusKey);
  };

  const handleStatusHeaderDrop = (e, targetStatusKey) => {
    const dragType = e.dataTransfer.getData('dragType');
    if (dragType !== 'status-column') return;
    e.preventDefault();
    const sourceStatusKey = e.dataTransfer.getData('statusKey');
    if (sourceStatusKey && sourceStatusKey !== targetStatusKey && onReorderStatuses) {
      onReorderStatuses(sourceStatusKey, targetStatusKey);
    }
    setDraggedStatus(null);
    setDragOverStatusHeader(null);
  };

  return (
    <div className="flex gap-4 h-full min-h-[400px] overflow-x-auto pb-4 pt-2 custom-scrollbar">
      {columns.map(col => {
        const colTasks = tasks.filter(t => t.status === col);
        const isDragOver = dragOverCol === col;
        const isHeaderTarget = dragOverStatusHeader === col && draggedStatus && draggedStatus !== col;
        return (
          <div key={col} 
            className={`w-[280px] shrink-0 bg-[#181b26] border rounded-md flex flex-col h-full max-h-full transition-colors ${isDragOver ? 'border-blue-500 bg-blue-500/5 shadow-lg shadow-blue-500/10' : 'border-slate-700'}`}
            onDragOver={(e) => handleDragOver(e, col)} 
            onDragLeave={(e) => handleDragLeave(e, col)}
            onDrop={(e) => handleDrop(e, col)}
          >
            <div
              draggable={canReorderStatuses && col !== 'VAZIO'}
              onDragStart={(e) => handleStatusHeaderDragStart(e, col)}
              onDragEnd={() => { setDraggedStatus(null); setDragOverStatusHeader(null); }}
              onDragOver={(e) => handleStatusHeaderDragOver(e, col)}
              onDrop={(e) => handleStatusHeaderDrop(e, col)}
              className={`p-3 border-b border-slate-700 font-bold text-sm rounded-t-md ${col === 'VAZIO' ? 'bg-slate-700 text-white' : statusColors[col]} ${canReorderStatuses && col !== 'VAZIO' ? 'cursor-move' : ''} ${isHeaderTarget ? 'ring-2 ring-blue-400 ring-inset' : ''}`}
            >
              {col === 'VAZIO' ? 'SEM STATUS' : col} <span className="ml-2 opacity-70 text-xs font-normal">({colTasks.length})</span>
            </div>
            <div className="flex-1 p-3 flex flex-col gap-3 overflow-y-auto custom-scrollbar">
              {colTasks.map(task => {
                const hasUpdates = task.updates && task.updates.length > 0;
                const progress = Number(task.progress || 0);
                const progressColor = progress >= 80 ? 'bg-emerald-500' : progress >= 50 ? 'bg-blue-500' : progress >= 25 ? 'bg-amber-500' : 'bg-slate-500';
                const hasActivity = Array.isArray(task.activityLog) && task.activityLog.length > 0;
                const slaDays = Number(task.slaDays || 5);
                const lastActivityTs = task.lastActivityAt || task.lastStatusChangeAt || task.dataCriacao;
                let daysSince = 0;
                if (lastActivityTs) {
                  const t = new Date(lastActivityTs).getTime();
                  if (Number.isFinite(t)) daysSince = (Date.now() - t) / (1000 * 60 * 60 * 24);
                }
                const isStagnant = daysSince >= slaDays;
                const stagnantBadge = isStagnant ? (
                  <div className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-500/30 text-red-200 border border-red-500/40 inline-flex items-center gap-1" title={`Sem movimento há ${daysSince.toFixed(0)} dia(s) (SLA: ${slaDays})`}>
                    Parada há {daysSince.toFixed(0)}d
                  </div>
                ) : null;
                return (
                <div key={task.id} draggable
                  onDragStart={(e) => handleDragStart(e, task.id)}
                  onDragEnd={handleDragEnd}
                  className={`bg-slate-800 border rounded p-3 shadow-sm hover:border-blue-500 cursor-grab active:cursor-grabbing transition-colors group relative ${isStagnant ? 'border-red-500/50' : 'border-slate-600'}`}
                >
                  {hasUpdates ? <div className="absolute top-2 right-2 w-2 h-2 bg-blue-500 rounded-full" title="Possui atualizações"></div> : null}
                  <div className="font-medium text-slate-200 mb-1.5 pr-4 truncate" title={task.elemento}>{task.elemento}</div>
                  {stagnantBadge ? <div className="mb-1.5">{stagnantBadge}</div> : null}
                  {(() => {
                    const config = taskStatusConfig?.[task.status];
                    const slaDays = config?.slaDays ?? 0;
                    const controlaPrazo = config?.controlaPrazo ?? false;
                    if (!controlaPrazo || !task.dataStatus) return null;
                    const [sy, sm, sd] = task.dataStatus.split('-').map(Number);
                    const now = new Date();
                    const hoje = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                    const dataEntrada = new Date(sy, sm - 1, sd);
                    const daysInStatus = Math.floor((hoje - dataEntrada) / 86400000);
                    const remaining = slaDays - daysInStatus;
                    const isDentro = remaining >= 0;
                    if (task.status === 'FINALIZADO') return null;
                    return (
                      <div className={`mb-2 text-[10px] font-bold px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${isDentro ? 'bg-green-500/20 text-green-300 border border-green-500/40' : 'bg-red-500/30 text-red-200 border border-red-500/40'}`}>
                        {isDentro ? `${remaining}d restantes` : `${Math.abs(remaining)}d atrasado`}
                      </div>
                    );
                  })()}
                  <div className="mb-2.5">
                    <div className="flex items-center justify-between text-[10px] text-slate-400 mb-1">
                      <span className="font-semibold">{progress}%</span>
                      {hasActivity ? <span className="opacity-60">{task.activityLog.length} evento(s)</span> : null}
                    </div>
                    <div className="h-1.5 bg-slate-900 rounded-full overflow-hidden">
                      <div className={`h-full ${progressColor} transition-all duration-300`} style={{ width: `${progress}%` }} />
                    </div>
                  </div>
                  <div className="flex justify-between items-center text-xs text-slate-400">
                    <div className="flex items-center gap-3">
                      <div className="flex -space-x-1">
                        {(task.equipe || []).length > 0 ? (task.equipe || []).map((m, i) => {
                          const memberData = allMembers.find(mem => mem.initials === m);
                          const bgColor = memberData ? memberData.color : 'bg-slate-500';
                          return <div key={i} className={`w-6 h-6 rounded-full ${bgColor} border border-slate-800 flex items-center justify-center text-[10px] font-bold text-white`}>{m}</div>
                        }) : <div className="w-6 h-6 rounded-full bg-slate-700 border border-slate-600 border-dashed flex items-center justify-center"><User size={10} className="text-slate-400"/></div>}
                      </div>
                      <div className="p-1.5 rounded hover:bg-slate-700 cursor-pointer text-slate-500 hover:text-blue-400 opacity-0 group-hover:opacity-100 transition-opacity" onClick={() => onOpenNotes(task.id)}><MessageSquare size={14} /></div>
                      <div className="p-1.5 rounded hover:bg-slate-700 cursor-pointer text-slate-500 hover:text-emerald-400 opacity-0 group-hover:opacity-100 transition-opacity" onClick={() => setProgressTaskId(task.id)} title="Progresso e timeline"><Clock size={14} /></div>
                    </div>
                    <span className="bg-slate-900 px-2 py-1 rounded">{task.entrega && task.entrega.includes('-') ? `${task.entrega.split('-')[2]}/${task.entrega.split('-')[1]}` : (task.entrega || 'Sem data')}</span>
                  </div>
                </div>
              )})}
            </div>
          </div>
        );
      })}
      {progressTaskId ? (
        <TaskProgressModal
          task={tasks.find((t) => t.id === progressTaskId) || { id: progressTaskId }}
          currentUser={currentUser}
          onClose={() => setProgressTaskId(null)}
          onUpdate={(updated) => { onUpdateTask && onUpdateTask(updated); }}
        />
      ) : null}
    </div>
  );
};

export default KanbanBoard;
